/* oxlint-disable local-rules/enforce-generate-path -- API endpoint URLs, not navigation paths */
import type { SettingsFormValues } from '@/features/pipelines/subscription-editor';
import { api } from '@/services/cdp-api';
import type {
  Destination,
  DestinationErrorSamples,
  DestinationErrorsSummary,
  DestinationErrorType,
  DestinationEvent,
  DestinationExport,
  DestinationMetrics,
  DestinationOption,
  DestinationSubscription,
  DestinationTestExchange,
} from '.';
import type {
  OAuthOpenResponse,
  OauthProvidersConfig,
  OauthCredentialConfig as OauthConnection,
} from '@/features/pipelines/oauth';
import type { DestinationDefinition } from '@customerio/cdp-action-destinations';
import type { MetricsDuration } from '@/utils/time';
import type { JsonObject } from '@/utils/json';

type ContinuationRequestExtended = {
  after?: string;
  before?: string;
};

type Meta = {
  continuation?: {
    first?: string;
    last?: string;
  };
  pagination?: {
    page: number;
    total: number;
  };
};

export type DestinationId = number | 'journeys';

export const listDestinationOptions = async (workspaceId: number) => {
  const { data } = await api.get<{ destinations: DestinationOption[] }>(
    `/workspaces/${workspaceId}/available_destinations`,
  );
  return data.destinations;
};

export const getDestination = async (workspaceId: number, destinationId: DestinationId) => {
  const { data } = await api.get<Destination>(`/workspaces/${workspaceId}/destinations/${destinationId}`);
  return data;
};

export const createDestination = async (
  workspaceId: number,
  {
    name,
    enabled = true,
    enable_actions = false,
    optionId,
    replayData = false,
    sourceIds = [],
    settings,
  }: {
    name: string;
    enabled?: boolean;
    enable_actions?: boolean;
    optionId: string;
    replayData?: boolean;
    sourceIds?: number[];
    settings: SettingsFormValues['settings'];
  },
) => {
  const { data } = await api.post<Destination>(`/workspaces/${workspaceId}/destinations`, {
    destination: {
      name,
      enabled,
      enable_actions,
      option_id: optionId,
      replay_data: replayData,
      source_ids: sourceIds,
      settings,
    },
  });
  return data;
};

export const updateDestination = async (
  workspaceId: number,
  destinationId: DestinationId,
  {
    name,
    enabled,
    settings,
  }: {
    name: string;
    enabled: boolean;
    settings: SettingsFormValues['settings'];
  },
) => {
  const { data } = await api.put<Destination>(`/workspaces/${workspaceId}/destinations/${destinationId}`, {
    destination: {
      name,
      enabled,
      settings,
    },
  });
  return data;
};

export const deleteDestination = async (workspaceId: number, destinationId: DestinationId) => {
  const { data } = await api.delete(`/workspaces/${workspaceId}/destinations/${destinationId}`);
  return data;
};

export const listDestinations = async (
  workspaceId: number,
  filters?: { sourceId?: number | null; inverse?: boolean | null },
) => {
  const { data } = await api.get<{ destinations: Destination[] }>(`/workspaces/${workspaceId}/destinations`, {
    params: {
      source_id: filters?.sourceId ?? undefined,
      inverse: filters?.inverse ?? undefined,
    },
  });
  return data.destinations ?? [];
};

export const getDestinationMetrics = async (
  workspaceId: number,
  destinationId: DestinationId,
  duration?: MetricsDuration,
) => {
  const { data } = await api.get<{ metrics: DestinationMetrics }>(
    `/workspaces/${workspaceId}/destinations/${destinationId}/metrics`,
    {
      params: {
        duration,
      },
    },
  );
  return data.metrics;
};

export const getDestinationOAuthProviderConfig = async (
  workspaceId: number,
  optionID: string,
  settings: SettingsFormValues['settings'] | null,
) => {
  const stringifiedSettings = JSON.stringify(settings ?? '{}');
  const { data } = await api.get<OauthProvidersConfig>(
    `/workspaces/${workspaceId}/destinations/connect?option_id=${optionID}&settings=${encodeURIComponent(
      stringifiedSettings,
    )}`,
  );
  return data;
};

export const getDestinationOAuthConnection = async (workspaceId: number, destinationId: DestinationId) => {
  const { data } = await api.get<OauthConnection>(`/workspaces/${workspaceId}/destinations/${destinationId}/connect`);
  return data;
};

export const createDestinationOAuthConnection = async (
  workspaceId: number,
  destinationId: DestinationId,
  payload: OAuthOpenResponse,
) => {
  await api.post(`/workspaces/${workspaceId}/destinations/${destinationId}/connect`, payload);
};

export const forceDestinationOAuthTokenRefresh = async (
  workspaceId: number,
  destinationId: DestinationId,
  payload: JsonObject,
) => {
  await api.post(`/workspaces/${workspaceId}/destinations/${destinationId}/force_token_refresh`, payload);
};

export const deleteDestinationOAuthConnection = async (workspaceId: number, destinationId: DestinationId) => {
  await api.delete(`/workspaces/${workspaceId}/destinations/${destinationId}/connect`);
};

export const getDestinationEvents = async (
  workspaceId: number,
  destinationId: DestinationId,
  params: {
    sourceId?: number | null;
    limit?: number;
    continuation?: ContinuationRequestExtended;
    filter_status_codes?: string[];
    subscription_ids?: number[];
    search_text?: string;
  },
) => {
  const apiParams = {
    source_id: params.sourceId ?? undefined,
    limit: params.limit ?? 100,
    ...params.continuation,
    ...(params.filter_status_codes != null && {
      filter_status_codes: params.filter_status_codes,
    }),
    ...(params.subscription_ids != null && {
      destination_subscription_ids: params.subscription_ids,
    }),
    ...(params.search_text != null && { search_text: params.search_text }),
  };
  const { data } = await api.get(`/workspaces/${workspaceId}/destinations/${destinationId}/events`, {
    params: apiParams,
    paramsSerializer: {
      indexes: null,
    },
  });
  return data;
};

export const sendDestinationTestEvent = async (
  workspaceId: number,
  destinationId: DestinationId,
  payload: JsonObject,
) => {
  const { data } = await api.post<DestinationTestExchange | null>(
    `/workspaces/${workspaceId}/destinations/${destinationId}/send_test`,
    payload,
  );
  return data;
};

export const getDestinationOptionManifest = async (workspaceId: number, destinationOptionId: string) => {
  const { data } = await api.get<DestinationDefinition>(
    `/workspaces/${workspaceId}/action_destinations/${destinationOptionId}/manifest`,
  );
  return data;
};

export const listDestinationSubscriptions = async (workspaceId: number, destinationId: DestinationId) => {
  const { data } = await api.get<{ subscriptions: DestinationSubscription[] }>(
    `/workspaces/${workspaceId}/destinations/${destinationId}/subscriptions`,
  );
  return data.subscriptions;
};

export const listDestinationExports = async (
  workspaceId: number,
  destinationId: DestinationId,
  params: {
    limit?: number;
    page?: number;
  } = {},
) => {
  const apiParams = {
    limit: params.limit ?? 10,
    page: params.page,
  };
  const { data } = await api.get<{ exports: DestinationExport[]; meta: Meta }>(
    `/workspaces/${workspaceId}/destinations/${destinationId}/exports`,
    {
      params: apiParams,
    },
  );
  return data;
};

export const getDestinationSubscription = async (
  workspaceId: number,
  destinationId: DestinationId,
  subscriptionId: number,
) => {
  const { data } = await api.get<DestinationSubscription>(
    `/workspaces/${workspaceId}/destinations/${destinationId}/subscriptions/${subscriptionId}`,
  );
  return data;
};

export const createDestinationSubscription = async (
  workspaceId: number,
  destinationId: DestinationId,
  subscription: {
    action: string;
    mapping: string;
    name: string;
    subscribe: string;
    settings?: JsonObject;
  },
) => {
  const { data } = await api.post<DestinationSubscription>(
    `/workspaces/${workspaceId}/destinations/${destinationId}/subscriptions`,
    {
      subscription: {
        ...subscription,
        destination_id: destinationId,
      },
    },
  );
  return data;
};

export const updateDestinationSubscription = async (
  workspaceId: number,
  destinationId: DestinationId,
  subscriptionId: number,
  subscription: {
    action: string;
    enabled: boolean;
    mapping: string;
    name: string;
    subscribe: string;
    settings?: JsonObject;
  },
) => {
  const { data } = await api.put<DestinationSubscription>(
    `/workspaces/${workspaceId}/destinations/${destinationId}/subscriptions/${subscriptionId}`,
    { subscription },
  );
  return data;
};

export const toggleDestinationSubscription = async (
  workspaceId: number,
  destinationId: DestinationId,
  enabled: boolean,
) => {
  const { data } = await api.post<{ subscriptions: DestinationSubscription[] }>(
    `/workspaces/${workspaceId}/destinations/${destinationId}/subscriptions/toggle_all`,
    { enabled },
  );
  return data.subscriptions;
};

export const deleteDestinationSubscription = async (
  workspaceId: number,
  destinationId: DestinationId,
  subscriptionId: number,
) => {
  const { data } = await api.delete(
    `/workspaces/${workspaceId}/destinations/${destinationId}/subscriptions/${subscriptionId}`,
  );
  return data;
};

export const getDestinationSubscriptionPreview = async (
  workspaceId: number,
  {
    mapping,
    event,
  }: {
    mapping: JsonObject;
    event: JsonObject;
  },
) => {
  const { data } = await api.post<JsonObject>(`/workspaces/${workspaceId}/preview`, {
    mapping,
    event,
  });
  return data;
};

type GetDestinationStatusResponse = {
  status: 'no_recent_data' | 'enabled' | 'disabled';
  last_seen?: number;
};

export const getDestinationStatus = async (workspaceId: number, destinationId: DestinationId) => {
  const { data } = await api.get<GetDestinationStatusResponse>(
    `/workspaces/${workspaceId}/destinations/${destinationId}/status`,
  );
  const { status, last_seen: lastSeen } = data;
  return { status, lastSeen };
};

export const getDestinationsLastEvents = async (workspaceId: number) => {
  const { data } = await api.get<{
    statuses: { [key: string]: number };
  }>(`/workspaces/${workspaceId}/destinations/statuses`);
  return data.statuses;
};

export const getDestinationErrorsSummary = async (workspaceId: number, destinationId: DestinationId) => {
  const url = `/workspaces/${workspaceId}/destinations/${destinationId}/errors_summary`;
  const { data } = await api.get<DestinationErrorsSummary>(url);
  return data;
};

export const getDestinationErrorSamples = async (
  workspaceId: number,
  destinationId: DestinationId,
  errorType: DestinationErrorType,
) => {
  const url = `/workspaces/${workspaceId}/destinations/${destinationId}/errors/${errorType}`;
  const { data } = await api.get<DestinationErrorSamples>(url);
  return data;
};

export const getDestinationEventById = async (
  workspaceId: number,
  destinationId: DestinationId,
  eventId: string,
  params: {
    storeServerId?: string;
    storeKey?: string;
    truncate?: boolean;
  } = {},
) => {
  const apiParams = {
    store_server_id: params.storeServerId,
    store_key: params.storeKey,
    truncate: params.truncate,
  };
  const { data } = await api.get<DestinationEvent>(
    `/workspaces/${workspaceId}/destinations/${destinationId}/events/${eventId}`,
    {
      params: apiParams,
    },
  );
  return data;
};

export const validateDestinationOptionSettings = async (
  workspaceId: number,
  optionID: string,
  settings: SettingsFormValues['settings'] | null,
) => {
  await api.post(`/workspaces/${workspaceId}/action_destinations/${optionID}/validate`, { settings });
};

export const validateDestinationSettings = async (
  workspaceId: number,
  destinationId: DestinationId,
  settings: SettingsFormValues['settings'],
) => {
  await api.post(`/workspaces/${workspaceId}/destinations/${destinationId}/validate`, { settings });
};

export const getDestinationErrorById = async (
  workspaceId: number,
  destinationId: DestinationId,
  eventId: string,
  errorType: DestinationErrorType,
  params: {
    truncate?: boolean;
  } = {},
) => {
  const apiParams = {
    truncate: params.truncate,
  };
  const { data } = await api.get<DestinationEvent>(
    `/workspaces/${workspaceId}/destinations/${destinationId}/errors/${errorType}/${eventId}`,
    {
      params: apiParams,
    },
  );
  return data;
};

export const setAutotrackFlag = async (accountId: number, autotrackEnabled: boolean) => {
  const { data } = await api.post<{
    feature_flags: Record<string, boolean>;
  }>(`/accounts/${accountId}/toggle_auto_track`, { enable: autotrackEnabled });
  return data;
};
